import clsx from "clsx";

import type { WaitTime as WaitTimeType } from ".prisma/client";

import FormattedMinutes from "./FormattedMinutes";

import type { useOperatingHours as UseOperatingHoursType } from "~/hooks/useOperatingHours";

import { isWaitTimeStale } from "../helpers/isWaitTimeStale";

import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { Tooltip } from "~/components/ui";
import { ParsedLocation } from "@edwt/cms/prisma";

const WaitTime = ({
  waitTime,
  useOperatingHours,
  showWaitTimes,
  showStatus,
  location,
  overrideWaitTime,
}: {
  waitTime?: WaitTimeType | undefined;
  useOperatingHours: ReturnType<typeof UseOperatingHoursType>;
  showWaitTimes: boolean;
  showStatus: boolean;
  location: ParsedLocation;
  overrideWaitTime: string | boolean;
}) => {
  const { isOpen } = useOperatingHours;

  const colorClass = (() => {
    if (isOpen === false) return "text-gray";
    if (
      waitTime === undefined ||
      waitTime.waitTimeMinutes === null ||
      showWaitTimes === false ||
      showStatus === false
    )
      return "text-blue";
    if (waitTime?.status === "normal") return "text-green";
    if (waitTime?.status === "abnormal") return "text-red";
  })();

  // The Wait Time text content
  const WaitTimeText = () => {
    // Render different content based on the conditions
    switch (true) {
      case overrideWaitTime === "hidden":
        return <></>;
      // If location is closed
      case isOpen === false:
        return <div>Closed</div>;
      // If component has waitTimeMinutes, the wait time isn't stale and we are supposed to render wait times
      case waitTime?.waitTimeMinutes &&
        !isWaitTimeStale(waitTime) &&
        overrideWaitTime === false &&
        showWaitTimes:
        return (
          <>
            {/* Only render status icon next to wait time if we were told to */}
            {showStatus && (
              <>
                {waitTime.status === "normal" && (
                  <Tooltip text="Wait Times are typical for this location">
                    <FaCheckCircle className="m-1 h-full w-3" />
                  </Tooltip>
                )}
                {waitTime.status === "abnormal" && (
                  <Tooltip text="Wait Times are longer than usual at this location">
                    <FaExclamationCircle className="m-1 h-full w-3" />
                  </Tooltip>
                )}
              </>
            )}
            <div className="flex gap-1">
              <FormattedMinutes minutes={waitTime.waitTimeMinutes} />
            </div>
          </>
        );
      // Fallback text when an open location has no wait time data (or has been overridden), either:
      // 1. Tell users to contact for wait times
      case location.waitTimeFallback === "contact" &&
        overrideWaitTime === false:
      case overrideWaitTime === "contact":
        return (
          <div>
            <div>Contact</div>
            <div className="text-sm font-normal">for wait times</div>
          </div>
        );
      // 2. Fallback to show open status
      case location.waitTimeFallback === "open-status":
      case overrideWaitTime === "contact":
      // Also show just show open if any of the above conditions are not met
      default:
        return (
          <div>
            <div>Open</div>
          </div>
        );
    }
  };

  return (
    <div className="">
      <div
        className={clsx(
          "text flex items-center text-right text-2xl font-bold",
          colorClass,
        )}
      >
        <WaitTimeText />
      </div>
    </div>
  );
};

export default WaitTime;
